// src/components/FaceCamera.jsx
import React, { useRef, useEffect, useState } from 'react';
import * as faceapi from 'face-api.js';

export default function FaceCamera() {
  // Enable debug mode to see console logs
  const DEBUG_MODE = true;
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isModelLoading, setIsModelLoading] = useState(true);
  const [error, setError] = useState(null);
  const [faceDetected, setFaceDetected] = useState(false);
  const [facePosition, setFacePosition] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const animationIdRef = useRef(null);
  const modelsLoadedRef = useRef(false);
  const lastDetectionTimeRef = useRef(0);
  
  // Debug logging function
  const debugLog = (...args) => {
    if (DEBUG_MODE) {
      console.log(...args);
    }
  };

  useEffect(() => {
    async function loadModels() {
      try {
        setIsModelLoading(true);
        
        // Load the face detection model
        debugLog('Loading face detection models...');
        
        // First, make sure any existing models are properly disposed
        if (faceapi.nets.tinyFaceDetector.isLoaded) {
          debugLog('Disposing previously loaded model');
          await faceapi.nets.tinyFaceDetector.dispose();
        }
        
        // Define the model path - use the public folder path
        const modelPath = '/models';
        
        debugLog(`Loading model from: ${modelPath}`);
        
        // Load the model with explicit await
        await faceapi.nets.tinyFaceDetector.loadFromUri(modelPath);
        
        // Verify the model is loaded
        if (faceapi.nets.tinyFaceDetector.isLoaded) {
          debugLog('Successfully loaded face detection model');
          modelsLoadedRef.current = true;
          setIsModelLoading(false);
          debugLog('Face detection model loading complete');
        } else {
          throw new Error('Model loaded but isLoaded check failed');
        }
      } catch (err) {
        console.error('Error loading face detection models:', err);
        
        // Try one more time with a different approach
        try {
          debugLog('Trying alternate loading method...');
          
          // Create a new instance and load it
          const tinyFaceDetector = new faceapi.TinyFaceDetector();
          await tinyFaceDetector.load('/models');
          
          // If we get here, the model loaded successfully
          debugLog('Successfully loaded face detection model with alternate method');
          modelsLoadedRef.current = true;
          setIsModelLoading(false);
        } catch (altErr) {
          console.error('Error with alternate loading method:', altErr);
          setError('Kon de gezichtsdetectie modellen niet laden. Probeer de pagina te verversen.');
          setIsModelLoading(false);
        }
      }
    }

    async function setupCamera() {
      try {
        debugLog('Setting up camera...');
        
        // Use lower resolution for better performance
        const constraints = {
          video: {
            width: { ideal: 640 },
            height: { ideal: 480 },
            facingMode: "user"
          }
        };
        
        // Request camera access
        debugLog('Requesting camera access with constraints:', constraints);
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        debugLog('Camera access granted, stream:', stream);
        
        if (videoRef.current) {
          // Set the stream as the video source
          videoRef.current.srcObject = stream;
          
          // Set up event listeners for video
          videoRef.current.onloadedmetadata = () => {
            debugLog('Video metadata loaded, dimensions:', {
              width: videoRef.current.videoWidth,
              height: videoRef.current.videoHeight
            });
            
            // Start playing the video
            videoRef.current.play().then(() => {
              debugLog('Video playback started');
              setIsLoading(false);
              
              // Start face detection once video is ready and models are loaded
              if (modelsLoadedRef.current) {
                startFaceDetection();
              }
            }).catch(err => {
              console.error('Error starting video playback:', err);
            });
          };
          
          // Additional event listeners for debugging
          videoRef.current.onloadeddata = () => {
            debugLog('Video data loaded');
          };
          
          videoRef.current.onerror = (err) => {
            console.error('Video error:', err);
          };
        } else {
          debugLog('Video ref is not available');
        }
      } catch (err) {
        console.error('Camera setup error:', err);
        setError('Camera toegang geweigerd. Geef toestemming om de camera te gebruiken.');
        setIsLoading(false);
      }
    }

    // Load models and setup camera in parallel
    loadModels();
    setupCamera();

    // Start face detection when both video and models are ready
    function checkAndStartDetection() {
      debugLog('Checking if we can start detection:', {
        isLoading,
        isModelLoading,
        modelsLoaded: modelsLoadedRef.current,
        videoReady: videoRef.current && videoRef.current.readyState >= 2
      });
      
      // Start face detection if everything is ready
      if (!isLoading && videoRef.current && videoRef.current.readyState >= 2) {
        startFaceDetection();
        
        // If models aren't loaded yet, we'll still start the detection loop
        // and it will handle loading the models
        if (!modelsLoadedRef.current) {
          debugLog('Starting detection without models loaded - will load in detection loop');
        }
      }
    }

    // Check more frequently at the beginning to start detection as soon as possible
    const checkInterval = setInterval(checkAndStartDetection, 300);

    function startFaceDetection() {
      if (animationIdRef.current) return; // Already running
      
      debugLog('Starting face detection');
      
      // Make sure we have a clean start
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
        animationIdRef.current = null;
      }
      
      // Start the detection loop
      detectFaces();
      
      // Also set up a periodic check to ensure detection is running
      const ensureDetectionInterval = setInterval(() => {
        if (!animationIdRef.current && videoRef.current && modelsLoadedRef.current) {
          debugLog('Face detection stopped unexpectedly, restarting...');
          detectFaces();
        }
      }, 2000); // Check every 2 seconds
      
      // Clean up the interval when component unmounts
      return () => clearInterval(ensureDetectionInterval);
    }

    async function detectFaces() {
      try {
        // Check if video and canvas elements exist
        if (!videoRef.current || !canvasRef.current) {
          debugLog('Video or canvas not available yet');
          animationIdRef.current = requestAnimationFrame(detectFaces);
          return;
        }
        
        // Get video element
        const video = videoRef.current;
        
        // Make sure video is playing and has dimensions
        if (video.paused || video.ended || !video.videoWidth || !video.videoHeight) {
          debugLog('Video not ready yet:', {
            paused: video.paused,
            ended: video.ended,
            width: video.videoWidth,
            height: video.videoHeight,
            readyState: video.readyState
          });
          
          // Try to play the video if it's paused
          if (video.paused && video.readyState >= 2) {
            try {
              await video.play();
              debugLog('Automatically started video playback');
            } catch (playErr) {
              debugLog('Could not auto-play video:', playErr);
            }
          }
          
          animationIdRef.current = requestAnimationFrame(detectFaces);
          return;
        }
        
        // Get canvas element
        const canvas = canvasRef.current;
        
        // Set canvas size to match video
        if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          debugLog('Canvas size set to:', canvas.width, canvas.height);
        }
        
        // Throttle detection for performance (every 100ms)
        const now = Date.now();
        if (now - lastDetectionTimeRef.current > 100) {
          // Check if models are loaded - CRITICAL STEP
          if (!faceapi.nets.tinyFaceDetector.isLoaded) {
            debugLog('Face detection model not loaded, waiting for model to load...');
            
            // Don't try to load the model here - wait for the loadModels function to complete
            // This prevents race conditions where multiple loads are attempted simultaneously
            
            animationIdRef.current = requestAnimationFrame(detectFaces);
            return;
          }
          
          // Set the models loaded flag if the model is actually loaded
          if (!modelsLoadedRef.current && faceapi.nets.tinyFaceDetector.isLoaded) {
            modelsLoadedRef.current = true;
            debugLog('Face detection model is loaded and ready');
          }
          
          // Create detection options with lower threshold for better detection
          const detectionOptions = new faceapi.TinyFaceDetectorOptions({
            inputSize: 160,     // smaller for better performance
            scoreThreshold: 0.2 // lower threshold to detect more faces
          });
          
          try {
            // Run face detection on the video element
            debugLog('Running face detection...');
            const detections = await faceapi.detectAllFaces(video, detectionOptions);
            
            // Update face detection state
            const hasDetections = detections.length > 0;
            
            // Only update state if it changed to avoid unnecessary re-renders
            if (hasDetections !== faceDetected) {
              setFaceDetected(hasDetections);
            }
            
            if (hasDetections) {
              // Get the first face detected
              const face = detections[0].box;
              
              // Only update position if it changed significantly
              const positionChanged = 
                Math.abs(face.x - facePosition.x) > 5 || 
                Math.abs(face.y - facePosition.y) > 5 || 
                Math.abs(face.width - facePosition.width) > 5 || 
                Math.abs(face.height - facePosition.height) > 5;
                
              if (positionChanged) {
                setFacePosition({
                  x: face.x,
                  y: face.y,
                  width: face.width,
                  height: face.height
                });
              }
              
              // Log face detection occasionally
              if (Math.random() < 0.05) { // Only log 5% of detections to avoid console spam
                debugLog('Face detected:', face);
              }
            }
            
            lastDetectionTimeRef.current = now;
          } catch (err) {
            console.error('Face detection error:', err);
            
            // If we get a model loading error, set the flag to false
            if (err.message && err.message.includes('load model before inference')) {
              modelsLoadedRef.current = false;
              debugLog('Model not properly loaded, resetting flag');
            }
          }
        }
      } catch (err) {
        console.error('Unexpected face detection error:', err);
      }
      
      // Continue detection loop
      animationIdRef.current = requestAnimationFrame(detectFaces);
    }

    // Cleanup function
    return () => {
      debugLog('Component unmounting, cleaning up resources');
      clearInterval(checkInterval);
      
      if (animationIdRef.current) {
        debugLog('Canceling animation frame');
        cancelAnimationFrame(animationIdRef.current);
        animationIdRef.current = null;
      }
      
      // Stop camera on unmount
      const videoElement = videoRef.current;
      if (videoElement && videoElement.srcObject) {
        debugLog('Stopping camera tracks');
        videoElement.srcObject.getTracks().forEach(t => t.stop());
      }
      
      // Dispose face detection models
      if (faceapi.nets.tinyFaceDetector.isLoaded) {
        debugLog('Disposing face detection model');
        try {
          faceapi.nets.tinyFaceDetector.dispose();
        } catch (err) {
          console.error('Error disposing face detection model:', err);
        }
      }
    };
  }, [isLoading, isModelLoading]); // Dependencies

  // State for showing/hiding debug panel
  const [showDebug, setShowDebug] = useState(false);
  
  return (
    <div style={{ position: 'relative', width: '640px', height: '480px' }}>
      {/* Small debug toggle button */}
      {DEBUG_MODE && (
        <button 
          onClick={() => setShowDebug(!showDebug)}
          style={{
            position: 'absolute',
            top: 5,
            right: 5,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '3px 6px',
            fontSize: '10px',
            zIndex: 20,
            cursor: 'pointer'
          }}
        >
          {showDebug ? 'Hide Debug' : 'Debug'}
        </button>
      )}
      
      {/* Debug panel - only shown when toggled */}
      {DEBUG_MODE && showDebug && (
        <div style={{
          position: 'absolute',
          top: 30,
          right: 5,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          padding: '10px',
          fontSize: '12px',
          zIndex: 10,
          maxWidth: '300px',
          borderRadius: '4px'
        }}>
          <div><strong>Debug Info:</strong></div>
          <div>Models loaded (ref): {modelsLoadedRef.current ? 'Yes' : 'No'}</div>
          <div>Models loaded (API): {faceapi.nets.tinyFaceDetector.isLoaded ? 'Yes' : 'No'}</div>
          <div>Face detected: {faceDetected ? 'Yes' : 'No'}</div>
          <div>Video ready: {videoRef.current && videoRef.current.readyState === 4 ? 'Yes' : 'No'}</div>
          <div>Video dimensions: {videoRef.current ? `${videoRef.current.videoWidth}x${videoRef.current.videoHeight}` : 'N/A'}</div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
            <button 
              onClick={async () => {
                try {
                  if (!videoRef.current) {
                    alert('Video not ready yet');
                    return;
                  }
                  
                  // Check if model is loaded
                  if (!faceapi.nets.tinyFaceDetector.isLoaded) {
                    alert('Face detection model not loaded yet. Please wait...');
                    
                    // Try to load the model
                    try {
                      await faceapi.nets.tinyFaceDetector.loadFromUri('/models');
                      alert('Model loaded successfully. Trying detection now...');
                    } catch (loadErr) {
                      alert('Failed to load model: ' + loadErr.message);
                      return;
                    }
                  }
                  
                  // Create detection options with lower threshold
                  const detectionOptions = new faceapi.TinyFaceDetectorOptions({
                    inputSize: 160,
                    scoreThreshold: 0.2
                  });
                  
                  alert('Running face detection...');
                  
                  // Run detection
                  const detections = await faceapi.detectAllFaces(videoRef.current, detectionOptions);
                  
                  // Update state
                  const hasDetections = detections.length > 0;
                  setFaceDetected(hasDetections);
                  
                  if (hasDetections) {
                    const face = detections[0].box;
                    setFacePosition({
                      x: face.x,
                      y: face.y,
                      width: face.width,
                      height: face.height
                    });
                    alert('Face detected! Position: ' + JSON.stringify({
                      x: Math.round(face.x),
                      y: Math.round(face.y),
                      width: Math.round(face.width),
                      height: Math.round(face.height)
                    }));
                  } else {
                    alert('No face detected. Try adjusting your position or lighting.');
                  }
                } catch (err) {
                  console.error('Manual face detection error:', err);
                  alert('Error detecting face: ' + err.message);
                }
              }} 
              style={{
                padding: '5px',
                backgroundColor: '#00FF00',
                color: 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Test Face
            </button>
          </div>
        </div>
      )}
      
      {/* Loading indicator */}
      {isLoading && (
        <div style={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          width: '100%', 
          height: '100%', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          backgroundColor: 'rgba(0,0,0,0.7)',
          color: 'white',
          zIndex: 2
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '10px' }}>Camera wordt geladen...</div>
            <div style={{ 
              width: '50px', 
              height: '50px', 
              border: '5px solid #333', 
              borderTop: '5px solid #fff',
              borderRadius: '50%',
              margin: '0 auto',
              animation: 'spin 1s linear infinite'
            }}></div>
          </div>
        </div>
      )}
      
      {/* Error message */}
      {error && (
        <div style={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          width: '100%', 
          height: '100%', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          backgroundColor: 'rgba(255,0,0,0.3)',
          color: 'white',
          zIndex: 2,
          padding: '20px',
          textAlign: 'center'
        }}>
          {error}
        </div>
      )}
      
      {/* Status indicators */}
      <div style={{ 
        position: 'absolute', 
        top: 10, 
        right: 10, 
        display: 'flex',
        flexDirection: 'column',
        gap: '5px',
        zIndex: 2
      }}>
        {/* Face detection status */}
        <div style={{ 
          backgroundColor: faceDetected ? 'rgba(0,255,0,0.7)' : 'rgba(255,0,0,0.7)', 
          color: 'white',
          padding: '5px 10px',
          borderRadius: '4px',
          fontSize: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '5px'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: 'white',
            opacity: Math.floor(Date.now() / 500) % 2 ? 1 : 0.5
          }}></div>
          {faceDetected ? 'Gezicht Gedetecteerd' : 'Geen Gezicht'}
        </div>
      </div>
      
      <video
        ref={videoRef}
        style={{ 
          width: '100%', 
          height: '100%', 
          objectFit: 'cover',
          transform: 'scaleX(-1)', // Mirror the video for selfie view
          backgroundColor: '#000'
        }}
        width="640"
        height="480"
        autoPlay
        playsInline
        muted
        crossOrigin="anonymous"
      />
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          pointerEvents: 'none',
          transform: 'scaleX(-1)', // Mirror the canvas to match video
          zIndex: 1 // Ensure canvas is above video
        }}
      />
      
      {/* Detection boxes overlay - using a wrapper div to handle mirroring */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        transform: 'scaleX(-1)', // Mirror the entire container
        pointerEvents: 'none',
        zIndex: 3
      }}>
        {/* Green box for face */}
        {faceDetected && (
          <div style={{
            position: 'absolute',
            top: facePosition.y,
            left: facePosition.x,
            width: facePosition.width,
            height: facePosition.height,
            border: '4px solid #00FF00',
            boxSizing: 'border-box',
            backgroundColor: 'rgba(0, 255, 0, 0.1)', // Semi-transparent green fill
            boxShadow: '0 0 10px #00FF00' // Glow effect
          }}>
            {/* Add a label inside the box */}
            <div style={{
              position: 'absolute',
              top: '-25px',
              left: '0',
              backgroundColor: '#00FF00',
              color: 'black',
              padding: '2px 8px',
              fontSize: '12px',
              fontWeight: 'bold',
              borderRadius: '4px'
            }}>
              Gezicht
            </div>
          </div>
        )}
      </div>
      
      {/* Add CSS for animations */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}